import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import authService from '../services/authService';
import { passwordValidationRule, validatePassword, getPasswordStrength } from '../utils/passwordValidation';

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countries, setCountries] = useState([]);
  const { register: registerUser, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const password = watch('password');
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [passwordStrength, setPasswordStrength] = useState({ strength: 'weak', score: 0 });

  // Update password requirements in real-time
  useEffect(() => {
    if (password) {
      const validation = validatePassword(password);
      const strength = getPasswordStrength(password);
      
      setPasswordRequirements({
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
      });
      
      setPasswordStrength(strength);
    } else {
      setPasswordRequirements({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      });
      setPasswordStrength({ strength: 'weak', score: 0 });
    }
  }, [password]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countriesData = await authService.getCountries();
        // Ensure we have an array, fallback to default countries if API fails
        setCountries(Array.isArray(countriesData) ? countriesData : [
          { code: 'US', name: 'United States' },
          { code: 'CA', name: 'Canada' },
          { code: 'GB', name: 'United Kingdom' },
          { code: 'DE', name: 'Germany' },
          { code: 'FR', name: 'France' },
          { code: 'JP', name: 'Japan' },
          { code: 'AU', name: 'Australia' },
        ]);
      } catch (error) {
        console.error('Error fetching countries:', error);
        // Set default countries on error
        setCountries([
          { code: 'US', name: 'United States' },
          { code: 'CA', name: 'Canada' },
          { code: 'GB', name: 'United Kingdom' },
          { code: 'DE', name: 'Germany' },
          { code: 'FR', name: 'France' },
          { code: 'JP', name: 'Japan' },
          { code: 'AU', name: 'Australia' },
        ]);
      }
    };

    fetchCountries();
  }, []);

  const onSubmit = async (data) => {
    try {
      await registerUser(data);
      toast.success('Registration successful! Please check your email for verification.');
      navigate('/verify-otp', { state: { email: data.email } });
    } catch (error) {
      console.error('Registration error:', error);
      // The error handling is now done in the service layer, so we can use the error message directly
      const errorMessage = typeof error === 'string' ? error : (error.message || 'Registration failed');
      toast.error(errorMessage);
      if (errorMessage === 'Email Already Exist. Please Login') {
        navigate('/login');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center pb-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center pt-12">
                 
           <h2 className=" text-center text-2xl md:text-3xl font-extrabold  dark:text-white">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-white dark:text-white">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card card-body">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white dark:text-white">
                  First Name
                </label>
                <input
                  {...register('firstName', { required: 'First name is required' })}
                  type="text"
                  className="w-full"
                  placeholder="First name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white dark:text-white">
                  Last Name
                </label>
                <input
                  {...register('lastName', { required: 'Last name is required' })}
                  type="text"
                  className="w-full"
                  placeholder="Last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white dark:text-white">
                Email Address
              </label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                type="email"
                  className="w-full"
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white dark:text-white">
                Country
              </label>
              <select
                {...register('country', { required: 'Country is required' })}
                  className="w-full"
              >
                <option value="">Select your country</option>
                {Array.isArray(countries) && countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white dark:text-white">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('password', {
                    required: 'Password is required',
                    validate: passwordValidationRule
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full"
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-white" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
              
              {/* Password Requirements */}
              {password && (
                <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-medium text-white dark:text-white mb-2">
                    Password must contain:
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li className={`flex items-center ${passwordRequirements.length ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                      <span className="mr-2">{passwordRequirements.length ? '✓' : '○'}</span>
                      At least 8 characters
                    </li>
                    <li className={`flex items-center ${passwordRequirements.uppercase ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                      <span className="mr-2">{passwordRequirements.uppercase ? '✓' : '○'}</span>
                      One uppercase letter (A-Z)
                    </li>
                    <li className={`flex items-center ${passwordRequirements.lowercase ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                      <span className="mr-2">{passwordRequirements.lowercase ? '✓' : '○'}</span>
                      One lowercase letter (a-z)
                    </li>
                    <li className={`flex items-center ${passwordRequirements.number ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                      <span className="mr-2">{passwordRequirements.number ? '✓' : '○'}</span>
                      One number (0-9)
                    </li>
                    <li className={`flex items-center ${passwordRequirements.special ? 'text-green-600 dark:text-white' : 'text-white dark:text-white'}`}>
                      <span className="mr-2">{passwordRequirements.special ? '✓' : '○'}</span>
                      One special character (!@#$%^&*()_+-=[]{}|;:,.&lt;&gt;?)
                    </li>
                  </ul>
                  
                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-white dark:text-white">
                          Password Strength:
                        </span>
                        <span className={`text-xs font-semibold ${
                          passwordStrength.strength === 'strong' ? 'text-green-600 dark:text-white' :
                          passwordStrength.strength === 'medium' ? 'text-white dark:text-white' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                          {passwordStrength.strength.toUpperCase()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            passwordStrength.strength === 'strong' ? 'bg-green-500' :
                            passwordStrength.strength === 'medium' ? 'bg-[#0E1696]' :
                            'bg-red-500'
                          }`}
                          style={{
                            width: `${(passwordStrength.score / 6) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white dark:text-white">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="w-full"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-white" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                {...register('acceptTerms', { required: 'You must accept the terms and conditions' })}
                id="accept-terms"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="accept-terms" className="ml-2 block text-sm text-white dark:text-white">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/terms#privacy-policy" className="text-primary-600 hover:text-primary-500">
                  Privacy Policy
                </Link>
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-red-600">{errors.acceptTerms.message}</p>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2 rounded-full disabled:opacity-50 !text-white !bg-[#0E1696] disabled:cursor-not-allowed flex justify-center items-center"
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
